import { FhirResourceTypes } from '../constants/FhirResourceTypes';
import { FhirBinary } from '../types/FhirBinary';
import moment from 'moment';
import dateUtils from './dateUtils';

const ALLERGY_ACTIVE_HEADING = 'Active:';
const ALLERGY_INACTIVE_HEADING = 'Inactive:';
const ALLERGY_INACTIVE_STATUSES = ['inactive', 'resolved', 'refuted', 'entered-in-error'];
const MEDICATION_ENDED_STATUSES = [
  'completed',
  'stopped',
  'cancelled',
  'not-taken',
  'entered-in-error'
];

const extractAllergyItems = (entries: any): string[] => {
  const activeItems: string[] = [];
  const inactiveItems: string[] = [];

  const truncateDate = (value?: string) =>
    value && value.length > 10 && value[10] === 'T' ? value.substring(0, 10) : value;

  entries.forEach((entry: any) => {
    const resource = entry.resource;
    const text = resource?.code?.text?.trim();
    if (!text) return;

    const allergyStatus =
      resource?.clinicalStatus?.coding?.[0]?.code?.trim()?.toLowerCase() ||
      resource?.clinicalStatus?.text?.trim()?.toLowerCase();

    const onset =
      truncateDate(resource?.onsetDateTime) ??
      truncateDate(resource?.onsetPeriod?.start) ??
      resource?.onsetString ??
      truncateDate(resource?.recordedDate);

    const reaction = resource?.reaction?.[0];

    const manifestation = reaction?.manifestation
      ?.map((item: any) => item?.text?.trim() || item?.coding?.[0]?.display?.trim())
      .filter(Boolean)
      .join(', ');

    const verification =
      resource?.verificationStatus?.text?.trim() ||
      resource?.verificationStatus?.coding?.[0]?.display?.trim() ||
      resource?.clinicalStatus?.text?.trim() ||
      resource?.clinicalStatus?.coding?.[0]?.display?.trim();

    const notes = [...(resource?.note ?? []), ...(reaction?.note ?? [])]
      .map((note: any) => note?.text?.replace(/\s*\n\s*/g, '; ').trim())
      .filter(Boolean)
      .filter((note: string, index: number, all: string[]) => all.indexOf(note) === index)
      .join('; ');

    const details = [reaction?.severity, manifestation, verification, notes]
      .filter(Boolean)
      .join(', ');

    let line = onset ? `${onset} - ${text}` : text;
    if (details) line += `, ${details}`;

    const target = ALLERGY_INACTIVE_STATUSES.includes(allergyStatus) ? inactiveItems : activeItems;
    if (!target.includes(line)) {
      target.push(line);
    }
  });

  const blocks: string[] = [];
  if (activeItems.length) blocks.push([ALLERGY_ACTIVE_HEADING, ...activeItems].join('\n'));
  if (inactiveItems.length) blocks.push([ALLERGY_INACTIVE_HEADING, ...inactiveItems].join('\n'));

  return blocks;
};

const CONDITION_ACTIVE_HEADING = 'Active';
const CONDITION_INACTIVE_HEADING = 'Inactive';
const CONDITION_INACTIVE_STATUSES = ['inactive', 'remission', 'resolved'];

const extractClinicalConditions = (entries: any): string[] => {
  const activeItems: string[] = [];
  const inactiveItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const clinicalText = resource?.code?.text?.trim();
    if (!clinicalText) return;

    const status =
      resource?.clinicalStatus?.coding?.[0]?.code?.toLowerCase() ||
      resource?.clinicalStatus?.text?.toLowerCase();

    const onsetRaw = resource?.onsetDateTime;
    const onset =
      onsetRaw && onsetRaw.length > 10 && onsetRaw[10] === 'T'
        ? onsetRaw.substring(0, 10)
        : onsetRaw;

    const notes = (resource?.note ?? [])
      .map((note: any) => note?.text?.replace(/\s*\n\s*/g, '; ').trim())
      .filter(Boolean)
      .join('; ');

    let line = onset ? `${onset} - ${clinicalText}` : clinicalText;
    if (notes) line += `, ${notes}`;

    const target = CONDITION_INACTIVE_STATUSES.includes(status) ? inactiveItems : activeItems;
    if (!target.includes(line)) {
      target.push(line);
    }
  });

  const blocks: string[] = [];
  if (activeItems.length) blocks.push([CONDITION_ACTIVE_HEADING, ...activeItems].join('\n'));
  if (inactiveItems.length) blocks.push([CONDITION_INACTIVE_HEADING, ...inactiveItems].join('\n'));

  return blocks;
};

const toDateOnly = (value?: string): string | undefined =>
  String(value ?? '').match(/\d{4}-\d{2}-\d{2}/)?.[0];

const conditionOnsetSummary = (condition: any): string | undefined => {
  if (condition?.onsetAge) {
    const unit = condition.onsetAge.unit ?? '';
    const ageStr = unit.startsWith(String(condition.onsetAge.value))
      ? unit
      : `${condition.onsetAge.value} ${unit}`.trim();
    return `Onset age: ${ageStr}`;
  }

  if (condition?.onsetRange) {
    return `Onset range: ${condition.onsetRange.low?.value ?? ''}-${
      condition.onsetRange.high?.value ?? ''
    } ${condition.onsetRange.low?.unit ?? ''}`.trim();
  }

  if (condition?.onsetString && !toDateOnly(condition.onsetString)) {
    return `Onset: ${condition.onsetString}`;
  }

  return undefined;
};

const extractFamilMemberHistoryItems = (entries: any): string[] => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const historyRelationship = resource?.relationship?.text;

    const historyName = resource?.name?.trim();

    const historyText =
      historyName && historyRelationship && historyName !== historyRelationship
        ? `${historyName} (${historyRelationship})`
        : historyName || historyRelationship || '';

    const age = resource?.ageAge
      ? `${resource.ageAge.value} ${resource.ageAge.unit ?? ''}`.trim()
      : resource?.ageRange
      ? `${resource.ageRange.low?.value ?? ''}-${resource.ageRange.high?.value ?? ''} ${
          resource.ageRange.low?.unit ?? ''
        }`.trim()
      : resource?.ageString
      ? resource.ageString
      : undefined;

    const deceasedBoolean = resource?.deceasedBoolean;
    const deceasedAge = resource?.deceasedAge
      ? `${resource.deceasedAge.value} ${resource.deceasedAge.unit ?? ''}`.trim()
      : undefined;
    const deceasedDate = resource?.deceasedDate;
    const deceased = deceasedBoolean
      ? 'yes'
      : deceasedAge
      ? `age ${deceasedAge}`
      : deceasedDate
      ? `${deceasedDate}`
      : undefined;

    const conditions = resource?.condition ?? [];

    const conditionSummary = conditions
      .map((c: any) => {
        const condText = c.code?.text || c.code?.coding?.[0]?.display;
        return [condText, conditionOnsetSummary(c)].filter(Boolean).join(', ');
      })
      .filter(Boolean)
      .join('; ');

    if (!historyText) return;

    const outputStr = [
      historyText,
      conditionSummary,
      age ? `Age: ${age}` : undefined,
      deceased ? `Deceased: ${deceased}` : undefined
    ]
      .filter(Boolean)
      .join(', ');

    if (!uniqueItems.includes(outputStr)) {
      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractMedicationItems = (entries: any) => {
  const strengthRegex =
    /(\d+\.?\d*\s*(MG|MCG|MEQ|ML|UNIT|IU)(\s*\/\s*\d+\.?\d*\s*(MG|MCG|MEQ|ML|UNIT|IU))?)/i;

  const getMedicationStrength = (...candidates: (string | undefined)[]) => {
    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }

      const match = candidate.match(strengthRegex);
      if (match?.[1]) {
        return match[1].trim();
      }
    }

    return undefined;
  };

  const cleanMedicationName = (name: string) => {
    return name
      .replace(strengthRegex, '')
      .replace(/\s*\([^)]*\)\s*$/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[|,;:\-]+$/g, '')
      .trim();
  };

  const normalizeMedicationName = (name: string) => cleanMedicationName(name).toLowerCase();

  const normalizeMedicationStrength = (strength: string | undefined) => {
    return (strength || '').replace(/\s+/g, '').toLowerCase();
  };

  const truncateDate = (value: string | undefined) =>
    value && value.length > 10 && value[10] === 'T' ? value.substring(0, 10) : value;

  const activeMedicationsByKey = new Map<string, string>();
  const pastMedicationsByKey = new Map<string, string>();

  const setMedicationByKey = (target: Map<string, string>, key: string, output: string) => {
    const existing = target.get(key);
    if (!existing || output.length > existing.length) {
      target.set(key, output);
    }
  };

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    if (
      resource?.resourceType !== FhirResourceTypes.MedicationRequest &&
      resource?.resourceType !== FhirResourceTypes.MedicationStatement
    ) {
      return;
    }

    const medtext = resource?.medicationCodeableConcept?.text?.trim();
    const medCoding = resource?.medicationCodeableConcept?.coding?.[0];
    const medCodingDisplay = medCoding?.display?.trim();
    const medCode = medCoding?.code;
    const medReferenceDisplay = resource?.medicationReference?.display?.trim();
    const medName = medtext || medCodingDisplay || medReferenceDisplay || medCode;
    const medStatus = resource?.status;
    const medPeriod = resource?.effectivePeriod ?? resource?.dispenseRequest?.validityPeriod;
    const medPeriodEnd = medPeriod?.end ? new Date(medPeriod.end) : undefined;
    const hasEnded =
      medPeriodEnd !== undefined &&
      !isNaN(medPeriodEnd.getTime()) &&
      medPeriodEnd.getTime() < Date.now();
    const isActive =
      medStatus === 'active' || (!hasEnded && !MEDICATION_ENDED_STATUSES.includes(medStatus));

    const dosageEntry = resource?.dosageInstruction?.[0] ?? resource?.dosage?.[0];

    const doseAndRate = dosageEntry?.doseAndRate?.[0];
    const doseValue = doseAndRate?.doseQuantity?.value;
    const doseUnit = doseAndRate?.doseQuantity?.unit;
    const doseQuantity =
      doseValue !== undefined && doseUnit ? `${doseValue} ${doseUnit}` : undefined;

    const sig = dosageEntry?.text?.trim();
    const dosageDisplay = sig || doseQuantity;

    const strength = getMedicationStrength(medCodingDisplay, medtext, medReferenceDisplay);

    const formatPeriod = (period: any) => {
      if (!period?.start) {
        return undefined;
      }
      return period.end && period.end !== period.start
        ? `${truncateDate(period.start)} – ${truncateDate(period.end)}`
        : truncateDate(period.start);
    };

    const periodDate =
      formatPeriod(resource?.effectivePeriod) ??
      formatPeriod(resource?.dispenseRequest?.validityPeriod);

    const medDate =
      periodDate ?? truncateDate(resource?.authoredOn) ?? truncateDate(resource?.dateAsserted);

    if (medName) {
      const nameStrengthMatch = medName.match(strengthRegex);
      const displayName =
        nameStrengthMatch?.index !== undefined
          ? [
              medName
                .slice(0, nameStrengthMatch.index)
                .replace(/\s*\([^)]*\)\s*$/g, '')
                .replace(/\s+/g, ' ')
                .replace(/[|,;:\-]+$/g, '')
                .trim(),
              nameStrengthMatch[1].trim()
            ]
              .filter(Boolean)
              .join(' ')
          : [cleanMedicationName(medName), strength].filter(Boolean).join(' ');

      let line = medDate ? `${medDate} - ` : '';
      line += displayName;
      if (dosageDisplay) line += `, ${dosageDisplay}`;

      const dedupeKey = `${normalizeMedicationName(medName)}|${normalizeMedicationStrength(
        strength
      )}`;
      setMedicationByKey(isActive ? activeMedicationsByKey : pastMedicationsByKey, dedupeKey, line);
    }
  });

  const result: string[] = [];
  if (activeMedicationsByKey.size) {
    result.push(`Active Medication:\n${Array.from(activeMedicationsByKey.values()).join('\n')}`);
  }
  if (pastMedicationsByKey.size) {
    result.push(`Past Medication:\n${Array.from(pastMedicationsByKey.values()).join('\n')}`);
  }

  return result;
};

const extractDefaultItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const text = entry.resource?.code?.text?.trim();
    if (text && !uniqueItems.includes(text)) {
      uniqueItems.push(text);
    }
  });

  return uniqueItems;
};

const ALLOWED_OBSERVATION_CATEGORIES = [
  'laboratory',
  'pathology',
  'imaging',
  'social-history',
  'alcohol-use',
  'tobacco-use'
];

const OBSERVATION_CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  laboratory: 'Laboratory',
  pathology: 'Pathology',
  imaging: 'Radiology',
  'social-history': 'Social History',
  'alcohol-use': 'Alcohol Use',
  'tobacco-use': 'Tobacco Use'
};

export const OBSERVATION_SOCIAL_HISTORY_HEADING =
  OBSERVATION_CATEGORY_DISPLAY_NAMES['social-history'];

export const stripSocialHistoryHeading = (content?: string): string =>
  (content ?? '')
    .split('\n\n')
    .map((block) => {
      const lines = block.split('\n');
      return lines[0]?.trim() === OBSERVATION_SOCIAL_HISTORY_HEADING
        ? lines.slice(1).join('\n')
        : block;
    })
    .filter((block) => block.trim())
    .join('\n\n');

const convertBrToNewlines = (text: string): string => {
  return text.replace(/\\\.?br\\?/g, '\n');
};

export const LAB_RESULT_CATEGORIES = ['laboratory', 'pathology'];
export const LAB_RESULT_COLUMN_HEADER = 'Test, Result (RSF Range)';
export const LAB_RESULT_ABNORMAL_MARKER = '*';

export const PATHOLOGY_CATEGORY = 'pathology';

export const expandObservationSpacing = (content?: string): string =>
  (content ?? '')
    .split('\n')
    .filter((line) => line.trim())
    .join('\n\n');

export const collapseObservationSpacing = (content?: string): string =>
  (content ?? '')
    .split('\n')
    .filter((line) => line.trim())
    .join('\n');
export const PATHOLOGY_REPORT_HEADING_PREFIX = 'Pathology: ';

export const isPathologyReportHeading = (heading: string): boolean =>
  heading.startsWith(PATHOLOGY_REPORT_HEADING_PREFIX);

const toPathologyReportBody = (text?: string): string =>
  (text ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim())
    .join('\n')
    .trimEnd();

const toPathologyReportHtml = (resource: any): string | undefined => {
  const div = resource?.text?.div;
  if (typeof div !== 'string' || !/<[a-z][\s\S]*>/i.test(div)) return undefined;
  const collapsed = div.replace(/\r?\n\s*/g, ' ').trim();
  return collapsed || undefined;
};

const PATHOLOGY_DIAGNOSTIC_REPORT_CODES = ['pat', 'cp', 'cyto', 'histo', 'path', 'pathology'];

const collectPathologyObservationRefs = (diagnosticReports: any[]): Set<string> => {
  const refs = new Set<string>();

  (diagnosticReports ?? []).forEach((report: any) => {
    const isPathology = report?.category?.some((category: any) =>
      category?.coding?.some((coding: any) =>
        PATHOLOGY_DIAGNOSTIC_REPORT_CODES.includes(coding?.code?.toLowerCase())
      )
    );
    if (!isPathology) return;

    report?.result?.forEach((result: any) => {
      if (result?.reference) {
        refs.add(result.reference);
      }
    });
  });

  return refs;
};

export type FhirExtractionContext = {
  pathologyObservationRefs?: Set<string>;
  supportingInfoTypes?: Map<string, string>;
  composedEncounterRefs?: Set<string>;
};

const RISK_FACTOR_TYPES = ['TobaccoUse', 'AlcoholUse', 'DrugUse'];
const RISK_FACTOR_STATUS_LABEL = 'Status';
const UCUM_ANNOTATION_BRACES = /[{}]/g;

const formatRiskFactorQuantity = (quantity: any): string =>
  `${quantity.value} ${(quantity.unit ?? '').replace(UCUM_ANNOTATION_BRACES, '')}`.trim();

const extractComponentValue = (component: any): string | undefined => {
  if (component?.valueQuantity) {
    return `${component.valueQuantity.value} ${component.valueQuantity.unit ?? ''}`.trim();
  }
  if (component?.valueCodeableConcept) {
    return (
      component.valueCodeableConcept.text || component.valueCodeableConcept.coding?.[0]?.display
    );
  }
  if (component?.valueString) return component.valueString;
  if (component?.valueBoolean !== undefined) return `${component.valueBoolean}`;
  if (component?.valueInteger !== undefined) return `${component.valueInteger}`;
  return undefined;
};

const extractObservationComponents = (resource: any): string | undefined => {
  const parts = (resource?.component ?? [])
    .map((component: any) => {
      const name = component?.code?.text?.trim() || component?.code?.coding?.[0]?.display?.trim();
      const value = extractComponentValue(component);
      if (!value) return undefined;
      return name ? `${name}: ${value}` : value;
    })
    .filter(Boolean);

  return parts.length ? parts.join(', ') : undefined;
};

const extractObservationNotes = (resource: any): string | undefined => {
  const notes = (resource?.note ?? [])
    .map((note: any) => note?.text?.replace(/\s*\n\s*/g, '; ').trim())
    .filter(Boolean);

  return notes.length ? notes.join('; ') : undefined;
};

const collectComposedEncounterRefs = (compositions: any[]): Set<string> => {
  const refs = new Set<string>();

  (compositions ?? []).forEach((composition: any) => {
    if (composition?.resourceType !== FhirResourceTypes.Composition) return;
    const reference = composition?.encounter?.reference;
    if (reference) {
      refs.add(reference);
    }
  });

  return refs;
};

const isNarrativeObservation = (resource: any): boolean =>
  !resource?.referenceRange?.length && !resource?.valueQuantity;

const isAbnormalObservation = (resource: any): boolean => {
  const code = resource?.interpretation?.[0]?.coding?.[0]?.code;
  return !!code && code.toUpperCase() !== 'N';
};

const extractReferenceRange = (resource: any): string | undefined => {
  const range = resource?.referenceRange?.[0];
  if (!range) return undefined;

  if (range.text) return range.text.replace(/\s+/g, ' ').trim();

  const unit = range.low?.unit ?? range.high?.unit ?? '';
  const low = range.low?.value;
  const high = range.high?.value;

  if (low !== undefined && high !== undefined) return `${low} - ${high} ${unit}`.trim();
  if (low !== undefined) return `> ${low} ${unit}`.trim();
  if (high !== undefined) return `< ${high} ${unit}`.trim();

  return undefined;
};

const LAB_RESULT_SECTION_NAMES = LAB_RESULT_CATEGORIES.map(
  (category) => OBSERVATION_CATEGORY_DISPLAY_NAMES[category]
);

const ACCESSION_IDENTIFIER_CODE = 'ACSN';

const extractAccessionNumber = (resource: any): string | undefined => {
  const identifiers = resource?.identifier ?? [];
  const accession = identifiers.find((identifier: any) =>
    identifier?.type?.coding?.some(
      (coding: any) => coding?.code?.toUpperCase() === ACCESSION_IDENTIFIER_CODE
    )
  );
  return accession?.value ?? identifiers[0]?.value;
};

type LabResultGroup = {
  date?: string;
  results: { name: string; line: string; id?: string; note?: string }[];
  narratives: string[];
};

const getLabResultGroup = (
  groups: Map<string, LabResultGroup>,
  key: string,
  date?: string
): LabResultGroup => {
  const existing = groups.get(key);
  if (existing) {
    if (!existing.date) existing.date = date;
    return existing;
  }

  const group: LabResultGroup = { date, results: [], narratives: [] };
  groups.set(key, group);
  return group;
};

const buildLabResultBlocks = (groups: Map<string, LabResultGroup>): string[] => {
  const blocks: string[] = [];

  groups.forEach((group) => {
    const lines: string[] = [];

    if (group.date) lines.push(group.date);

    if (group.results.length || group.narratives.length) {
      lines.push(LAB_RESULT_COLUMN_HEADER);
    }

    if (group.results.length) {
      const sorted = [...group.results].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })
      );
      sorted.forEach((result) => {
        lines.push(result.line);
        if (result.note) lines.push(result.note);
      });
    }

    lines.push(...group.narratives);

    if (lines.length) blocks.push(lines.join('\n'));
  });

  return blocks;
};

const extractObservationItems = (
  entries: any,
  pathologyObservationRefs?: Set<string>,
  supportingInfoTypes?: Map<string, string>
) => {
  const categoryItems = new Map<string, string[]>();
  const labGroupsByCategory = new Map<string, Map<string, LabResultGroup>>();
  const pathologyReports: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    if (/^rad-\d+$/i.test(resource?.id ?? '')) {
      return;
    }

    const isPathology = !!pathologyObservationRefs?.has(
      `${FhirResourceTypes.Observation}/${resource?.id}`
    );

    const categoryCode = isPathology
      ? 'pathology'
      : resource?.category?.[0]?.coding?.[0]?.code?.toLowerCase() ||
        resource?.category?.[0]?.text?.toLowerCase();

    if (!categoryCode || !ALLOWED_OBSERVATION_CATEGORIES.includes(categoryCode)) {
      return;
    }

    const obsDisplayText =
      resource?.code?.text?.trim() || resource?.code?.coding?.[0]?.display?.trim();

    const effectiveRaw =
      resource?.effectiveDateTime ||
      (resource?.effectivePeriod
        ? `${resource.effectivePeriod.start ?? ''} - ${resource.effectivePeriod.end ?? ''}`.trim()
        : undefined);

    // Truncate datetime to date-only (YYYY-MM-DD)
    const effective =
      effectiveRaw && effectiveRaw.length > 10 && effectiveRaw[10] === 'T'
        ? effectiveRaw.substring(0, 10)
        : effectiveRaw;

    const rawValue = resource?.valueQuantity
      ? `${resource.valueQuantity.value} ${resource.valueQuantity.unit ?? ''}`.trim()
      : resource?.valueCodeableConcept
      ? resource.valueCodeableConcept.text || resource.valueCodeableConcept.coding?.[0]?.display
      : resource?.valueString
      ? resource.valueString
      : resource?.valueBoolean !== undefined
      ? `${resource.valueBoolean}`
      : resource?.valueInteger !== undefined
      ? `${resource.valueInteger}`
      : resource?.valueRange
      ? `${resource.valueRange.low?.value ?? ''}-${resource.valueRange.high?.value ?? ''} ${
          resource.valueRange.low?.unit ?? ''
        }`.trim()
      : resource?.valueRatio
      ? `${resource.valueRatio.numerator?.value ?? ''}/${
          resource.valueRatio.denominator?.value ?? ''
        }`
      : undefined;

    const value = rawValue ? convertBrToNewlines(rawValue) : undefined;

    const isRiskFactor = RISK_FACTOR_TYPES.includes(
      supportingInfoTypes?.get(`${FhirResourceTypes.Observation}/${resource?.id}`) ?? ''
    );

    const components = extractObservationComponents(resource);
    const notes = extractObservationNotes(resource);

    if (!obsDisplayText && !value && !components && !notes) return;

    const categoryName =
      OBSERVATION_CATEGORY_DISPLAY_NAMES[categoryCode] ||
      resource?.category?.[0]?.text?.trim() ||
      categoryCode;

    if (isRiskFactor) {
      const status = resource?.valueQuantity
        ? formatRiskFactorQuantity(resource.valueQuantity)
        : value && resource?.valueCodeableConcept
        ? `${RISK_FACTOR_STATUS_LABEL}: ${value}`
        : value;
      const line = [obsDisplayText, status, components, notes].filter(Boolean).join(', ');

      if (!categoryItems.has(categoryName)) {
        categoryItems.set(categoryName, []);
      }
      const items = categoryItems.get(categoryName)!;
      if (!items.includes(line)) {
        items.push(line);
      }
      return;
    }

    const isLabCategory = LAB_RESULT_CATEGORIES.includes(categoryCode);

    const labGroup = () => {
      if (!labGroupsByCategory.has(categoryName)) {
        labGroupsByCategory.set(categoryName, new Map());
      }
      const groupKey = extractAccessionNumber(resource) ?? effective ?? obsDisplayText ?? '';
      return getLabResultGroup(labGroupsByCategory.get(categoryName)!, groupKey, effective);
    };

    if (isLabCategory && !isNarrativeObservation(resource)) {
      const range = extractReferenceRange(resource);

      let line = obsDisplayText ? `${obsDisplayText}, ${value}` : `${value}`;
      if (range) line += ` (${range})`;
      if (isAbnormalObservation(resource)) line += ` ${LAB_RESULT_ABNORMAL_MARKER}`;

      const group = labGroup();
      const resourceId = resource?.id;
      const isDuplicate = resourceId
        ? group.results.some((result) => result.id === resourceId)
        : group.results.some((result) => result.line === line);

      if (!isDuplicate) {
        group.results.push({ name: obsDisplayText ?? '', line, id: resourceId, note: notes });
      }
      return;
    }

    if (categoryCode === PATHOLOGY_CATEGORY) {
      const body =
        toPathologyReportBody(value) ||
        toPathologyReportHtml(resource) ||
        toPathologyReportBody([components, notes].filter(Boolean).join('\n'));

      if (!body) return;

      const label = [effective, obsDisplayText].filter(Boolean).join(' - ') || categoryName;
      const block = `${PATHOLOGY_REPORT_HEADING_PREFIX}${label}\n${body}`;

      if (!pathologyReports.includes(block)) {
        pathologyReports.push(block);
      }
      return;
    }

    const details = [value, components, notes].filter(Boolean).join(', ');

    if (isLabCategory) {
      const narrative = [obsDisplayText, details].filter(Boolean).join(', ');
      const group = labGroup();
      if (narrative && !group.narratives.includes(narrative)) {
        group.narratives.push(narrative);
      }
      return;
    }

    let line = effective ? `${effective} - ` : '';
    if (obsDisplayText) line += obsDisplayText;
    if (details) line += `${obsDisplayText ? ', ' : ''}${details}`;

    if (!categoryItems.has(categoryName)) {
      categoryItems.set(categoryName, []);
    }
    const items = categoryItems.get(categoryName)!;
    if (!items.includes(line)) {
      items.push(line);
    }
  });

  const result: string[] = [];
  labGroupsByCategory.forEach((groups, categoryName) => {
    const body = buildLabResultBlocks(groups).join('\n');
    if (body) {
      result.push(`${categoryName}\n${body}`);
    }
  });
  result.push(...pathologyReports);
  categoryItems.forEach((items, categoryName) => {
    result.push(`${categoryName}\n${items.join('\n')}`);
  });

  return result;
};

const extractImmunizationItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const immText = resource?.vaccineCode?.text?.trim();

    const occurrenceRaw = resource?.occurrenceDateTime || resource?.occurrenceString;
    const occurrence =
      occurrenceRaw && occurrenceRaw.length > 10 && occurrenceRaw[10] === 'T'
        ? occurrenceRaw.substring(0, 10)
        : occurrenceRaw;

    const immDoseQuantity = resource?.doseQuantity
      ? `${resource.doseQuantity.value} ${resource.doseQuantity.unit ?? ''}`.trim()
      : undefined;

    if (!immText) return;

    let line = occurrence ? `${occurrence} - ` : '';
    line += immText;
    if (immDoseQuantity) line += `, ${immDoseQuantity}`;

    if (!uniqueItems.includes(line)) {
      uniqueItems.push(line);
    }
  });

  return uniqueItems;
};

const extractProcedureItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const procResource = entry.resource;

    const getCodingText = (obj: any) =>
      obj?.text || obj?.coding?.[0]?.display || obj?.coding?.[0]?.code;

    const formatDate = (value: any) => {
      if (!value) return '';
      const match = String(value).match(/\d{4}-\d{2}-\d{2}/);
      return match ? match[0] : String(value);
    };

    const text = procResource?.code?.coding?.[0]?.text?.trim() || getCodingText(procResource?.code);

    const performed = formatDate(
      procResource?.performedDateTime ||
        procResource?.performedPeriod?.start ||
        procResource?.performedString
    );

    const outcome = getCodingText(procResource?.outcome);

    const complication = procResource?.complication?.map(getCodingText).filter(Boolean).join(', ');

    if (text) {
      const summary = complication || outcome || 'no complications';

      const outputStr = `${performed ? `${performed} - ` : ''}${text}, ${summary}`;

      if (!uniqueItems.includes(outputStr)) {
        uniqueItems.push(outputStr);
      }
    }
  });

  return uniqueItems;
};

const REPORT_TIMESTAMP_LINE = /^d[dt]\s*:/i;
const REPORT_EXAM_DATE_LINE = /^exam\s*date\s*:/i;

const extractDiagnosticReportItems = (entries: any) => {
  const items: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    if (!/^rad-\d+$/i.test(resource?.id ?? '')) {
      return;
    }

    const note = resource?.valueString;
    if (!note) return;

    const collected = toDateOnly(
      resource?.effectiveDateTime ?? resource?.effectivePeriod?.start ?? resource?.issued
    );

    const description =
      resource?.code?.text?.trim() || resource?.code?.coding?.[0]?.display?.trim();

    const body = convertBrToNewlines(note)
      .split('\n')
      .map((bodyLine: string) => bodyLine.trim())
      .filter(Boolean)
      .filter(
        (bodyLine: string) =>
          !REPORT_TIMESTAMP_LINE.test(bodyLine) && !REPORT_EXAM_DATE_LINE.test(bodyLine)
      )
      .join('\n');

    if (!body) return;

    const block = [collected, description, body].filter(Boolean).join('\n');

    if (!items.includes(block)) {
      items.push(block);
    }
  });

  return items;
};

const toSingleLineNarrative = (narrative?: string): string | undefined => {
  const collapsed = narrative?.replace(/\s*\n\s*/g, ' ').trim();
  return collapsed || undefined;
};

const formatEncounterDateTime = (value?: string): string | undefined => {
  if (!value) return value;
  const parsed = moment.parseZone(value, moment.ISO_8601);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD hh:mm:ss A') : value;
};

const extractCompositionNarrative = (resource: any): string | undefined => {
  const title =
    resource?.title?.trim() ||
    resource?.type?.text?.trim() ||
    resource?.type?.coding?.[0]?.display?.trim();

  const authored = formatEncounterDateTime(resource?.date);

  const sections = (resource?.section ?? [])
    .map((section: any) => {
      const sectionTitle = section?.title?.trim();
      const narrative = toSingleLineNarrative(section?.text?.div);
      return [sectionTitle, narrative].filter(Boolean).join('\n');
    })
    .filter(Boolean);

  if (!sections.length) return undefined;

  return [title, authored, ...sections].filter(Boolean).join('\n');
};

const extractEncounterItems = (entries: any, composedEncounterRefs?: Set<string>) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    // An encounter covered by a clinical note is represented by that note, not by a
    // summary of its metadata.
    if (
      resource?.resourceType === FhirResourceTypes.Encounter &&
      composedEncounterRefs?.has(`${FhirResourceTypes.Encounter}/${resource?.id}`)
    ) {
      return;
    }

    if (resource?.resourceType === FhirResourceTypes.Composition) {
      const narrativeBlock = extractCompositionNarrative(resource);
      if (narrativeBlock && !uniqueItems.includes(narrativeBlock)) {
        uniqueItems.push(narrativeBlock);
      }
      return;
    }

    const getCodingText = (obj: any) =>
      obj?.text || obj?.coding?.[0]?.display || obj?.coding?.[0]?.code;

    const getReferenceText = (ref: any) => ref?.display || ref?.reference;

    const text =
      resource?.type?.map(getCodingText).filter(Boolean).join(', ') ||
      getCodingText(resource?.serviceType) ||
      resource?.class?.display ||
      resource?.class?.code;

    const status = resource?.status;

    const classCode =
      resource?.class?.display || resource?.class?.code
        ? `${resource?.class?.display ?? resource?.class?.code}`
        : undefined;

    const type = resource?.type?.map(getCodingText).filter(Boolean).join(', ');

    const serviceType = getCodingText(resource?.serviceType);

    const priority = getCodingText(resource?.priority);

    const basedOn = resource?.basedOn?.map(getReferenceText).filter(Boolean).join(', ');

    const length = resource?.length
      ? `${resource.length.value ?? ''} ${resource.length.unit ?? ''}`.trim()
      : undefined;

    const reasonCode = resource?.reasonCode?.map(getCodingText).filter(Boolean).join(', ');

    const reasonReference = resource?.reasonReference
      ?.map(getReferenceText)
      .filter(Boolean)
      .join(', ');

    const diagnosis = resource?.diagnosis
      ?.map((d: any) => getReferenceText(d.condition))
      .filter(Boolean)
      .join(', ');

    const hospitalization = resource?.hospitalization
      ? [
          getCodingText(resource.hospitalization.admitSource),
          getCodingText(resource.hospitalization.dischargeDisposition)
        ]
          .filter(Boolean)
          .join(', ')
      : undefined;

    const participant = resource?.participant
      ?.map((p: any) => getReferenceText(p.individual))
      .filter(Boolean)
      .join(', ');

    const serviceProvider = getReferenceText(resource?.serviceProvider);

    const period = resource?.period?.start
      ? resource.period.end && resource.period.end !== resource.period.start
        ? `${formatEncounterDateTime(resource.period.start)} – ${formatEncounterDateTime(
            resource.period.end
          )}`
        : formatEncounterDateTime(resource.period.start)
      : undefined;

    if (text && !uniqueItems.includes(text)) {
      let outputStr = text;

      if (status) outputStr += ` | status: ${status}`;
      if (classCode) outputStr += ` | class: ${classCode}`;
      if (type) outputStr += ` | type: ${type}`;
      if (serviceType) outputStr += ` | serviceType: ${serviceType}`;
      if (priority) outputStr += ` | priority: ${priority}`;
      if (basedOn) outputStr += ` | basedOn: ${basedOn}`;
      if (length) outputStr += ` | length: ${length}`;
      if (reasonCode) outputStr += ` | reasonCode: ${reasonCode}`;
      if (reasonReference) outputStr += ` | reasonReference: ${reasonReference}`;
      if (diagnosis) outputStr += ` | diagnosis: ${diagnosis}`;
      if (hospitalization) outputStr += ` | hospitalization: ${hospitalization}`;
      if (participant) outputStr += ` | participant: ${participant}`;
      if (serviceProvider) outputStr += ` | serviceProvider: ${serviceProvider}`;
      if (period) outputStr += ` | period: ${period}`;

      const narrative = toSingleLineNarrative(resource?.text?.div);
      if (narrative) outputStr += `\n${narrative}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractItems = (entries: any, reportType: string, context?: FhirExtractionContext) => {
  switch (reportType) {
    case FhirResourceTypes.Encounter:
      return extractEncounterItems(entries, context?.composedEncounterRefs);
    case FhirResourceTypes.AllergyIntolerance:
      return extractAllergyItems(entries);
    case FhirResourceTypes.Condition:
      return extractClinicalConditions(entries);
    case FhirResourceTypes.FamilyMemberHistory:
      return extractFamilMemberHistoryItems(entries);
    case FhirResourceTypes.MedicationRequest:
    case FhirResourceTypes.MedicationStatement:
      return extractMedicationItems(entries);
    case FhirResourceTypes.Observation:
      return extractObservationItems(
        entries,
        context?.pathologyObservationRefs,
        context?.supportingInfoTypes
      );
    case FhirResourceTypes.Immunization:
      return extractImmunizationItems(entries);
    case FhirResourceTypes.Procedure:
      return extractProcedureItems(entries);
    case FhirResourceTypes.DiagnosticReport:
      return extractDiagnosticReportItems(entries);
    default:
      return extractDefaultItems(entries);
  }
};

const processBinaryData = (
  data: any,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>
) => {
  const entries = data?.entry ?? [];
  if (!entries || entries.length === 0) return;

  const unqiueBinaryItems: FhirBinary[] = [];
  if (!unqiueBinaryItems) return;

  entries.forEach((entry: any) => {
    if (entry?.resource?.contained?.length > 0) {
      entry?.resource?.contained.forEach((resource: FhirBinary) => {
        if (resource.resourceType === FhirResourceTypes.Binary) {
          unqiueBinaryItems.push(resource);
        }
      });
    }
  });

  if (unqiueBinaryItems?.length > 0) {
    setBinaryItems((prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const newItems = unqiueBinaryItems.filter((item) => !existingIds.has(item.id));
      return newItems.length > 0 ? [...prev, ...newItems] : prev;
    });
  }
};

const processFhirBundleResource = (
  data: any,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  reportType: string,
  setSelectedClinicalReports: React.Dispatch<React.SetStateAction<string[]>>,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>,
  context?: FhirExtractionContext
) => {
  const entries = data?.entry ?? [];
  if (!entries.length) return;

  const uniqueItems: string[] | undefined = extractItems(entries, reportType, context);

  //processBinaryData(data, setBinaryItems);

  if (!uniqueItems || uniqueItems?.length === 0) return;

  setSelectedClinicalReports((prev) => (prev.includes(reportType) ? prev : [...prev, reportType]));

  if (uniqueItems?.length > 0) {
    setContent((prev) => {
      const existing = prev
        .split('\n\n')
        .map((s) => s.trim())
        .filter(Boolean);

      if (
        reportType === FhirResourceTypes.DiagnosticReport ||
        reportType === FhirResourceTypes.Encounter
      ) {
        const merged = [...existing];
        uniqueItems.forEach((item) => {
          if (!merged.includes(item)) {
            merged.push(item);
          }
        });
        return merged.join('\n\n');
      }

      const merged = [...existing];
      uniqueItems.forEach((item) => {
        const newlineIdx = item.indexOf('\n');
        if (newlineIdx !== -1) {
          const header = item.substring(0, newlineIdx);
          if (isPathologyReportHeading(header)) {
            if (!merged.some((block) => block.split('\n')[0] === header)) {
              merged.push(item);
            }
            return;
          }
          const newLines = item
            .substring(newlineIdx + 1)
            .split('\n')
            .filter(Boolean);
          const existingIdx = merged.findIndex(
            (block) => block.startsWith(header + '\n') || block === header
          );
          if (existingIdx !== -1) {
            if (LAB_RESULT_SECTION_NAMES.includes(header)) {
              merged[existingIdx] = item;
              return;
            }
            const existingLines = merged[existingIdx].split('\n');
            newLines.forEach((line) => {
              if (!existingLines.includes(line)) {
                existingLines.push(line);
              }
            });
            merged[existingIdx] = existingLines.join('\n');
          } else {
            merged.push(item);
          }
        } else {
          if (!merged.includes(item)) {
            merged.push(item);
          }
        }
      });

      return merged.join('\n\n');
    });
  }
};

const processFhirResource = (
  data: any,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  reportType: string,
  setSelectedClinicalReports: React.Dispatch<React.SetStateAction<string[]>>,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>
) => {
  if (!data) return;

  const text = data?.code?.text?.trim();
  if (!text) return;

  const unqiueBinaryItems: FhirBinary[] = [];
  if (data?.contained?.length > 0) {
    data?.contained.forEach((resource: FhirBinary) => {
      if (resource.resourceType === FhirResourceTypes.Binary) {
        unqiueBinaryItems.push(resource);
      }
    });
  }

  setSelectedClinicalReports((prev) => {
    return prev.includes(reportType) ? prev : [...prev, reportType];
  });

  setContent((prev) => {
    const existing = prev
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    if (existing.includes(text)) return prev;

    return [...existing, text].join('\n');
  });

  // if (unqiueBinaryItems?.length > 0) {
  //   setBinaryItems((prev) => {
  //     const existingIds = new Set(prev.map((item) => item.id));
  //     const newItems = unqiueBinaryItems.filter((item) => !existingIds.has(item.id));
  //     return newItems.length > 0 ? [...prev, ...newItems] : prev;
  //   });
  // }
};

const processDocumentReferenceResource = (data: any): FhirBinary => {
  const attachment = data?.content?.[0]?.attachment;

  return {
    contentType: attachment?.contentType,
    data: attachment?.url,
    id: data?.id,
    resourceType: 'Binary'
  };
};

const FhirResourceUtils = {
  processFhirBundleResource,
  processFhirResource,
  processDocumentReferenceResource,
  collectPathologyObservationRefs,
  collectComposedEncounterRefs
};

export default FhirResourceUtils;
